import { useEffect, useRef } from 'react'
import { usePubsub } from '../../redux/pubsub'
import { shallow as shallowEquals } from 'zustand/shallow'
import { NoopStringSerializer, Serializer } from './serializers'

const UNSET = Symbol('unset')

type PubSubConfig<T> = {
  enabled: boolean
  sourceId: string
  publish?: PubConfig<T>
  subscribe?: SubConfig<T>
  isEqual?: (a: T, b: T) => boolean
} & (T extends string ? {
  serializer?: never
} : {
  serializer: Serializer<T>
})

type PubConfig<T> = {
  topic: string | null | undefined
  currentValue: T
}

type SubConfig<T> = {
  topic: string | null | undefined
  handleValue: (value: T) => void
  handleRepeatedValues?: boolean
  delayHandle?: boolean
}

export function useSyncPubSub<T>(conf: PubSubConfig<T>) {
  const [ variables, publish ] = usePubsub()
  const pubsubValue = getPubSubValue(variables, conf.subscribe?.topic)
  const pubsubValueTimestamp = getPubSubValue(variables, `${conf.subscribe?.topic}Timestamp`)

  // make sure to point to the latest conf when used in a closure
  const confRef = useRef(conf)
  confRef.current = conf

  const lastReceivedValueRef = useRef<T | typeof UNSET>(UNSET)

  const delayedSubscribeValuesRef = useRef<T[]>([])

  // Publish
  useEffect(() => {
    const conf = confRef.current
    if (!conf.enabled) return
    if (!conf.publish?.topic) return

    const currentValue = conf.publish.currentValue

    // Prevent feedback loop
    const prev = lastReceivedValueRef.current
    if (prev !== UNSET) {
      const isEqual = conf.isEqual ?? shallowEquals
      if (isEqual(currentValue, prev)) return
    }
    lastReceivedValueRef.current = currentValue

    try {
      const string = getSerializer(conf).serialize(currentValue)
      const actionId = `${conf.sourceId} -- sync`
      publish(conf.publish.topic, string, actionId)
    } catch (e) {
      console.error(`Error publishing value to pubsub topic ${conf.publish.topic}`, currentValue, e)
    }
  }, [ confRef, conf.publish?.currentValue, publish ])

  const timestampDep = (conf.subscribe?.handleRepeatedValues ?? true)
    ? pubsubValueTimestamp
    : null
  // Subscribe
  useEffect(() => {
    const conf = confRef.current
    if (!conf.enabled) return
    if (!conf.subscribe?.topic) return

    try {
      const value = getSerializer(conf).deserialize(pubsubValue)
      if (conf.subscribe.delayHandle) {
        delayedSubscribeValuesRef.current.push(value)
      } else {
        conf.subscribe.handleValue(value)
      }
    } catch (e) {
      console.error(`Error applying value from pubsub subscription topic ${conf.subscribe.topic}: "${pubsubValue}"`, e)
    }
  }, [ confRef, pubsubValue, timestampDep ])

  // Delayed subscribe
  useEffect(() => {
    const conf = confRef.current
    if (!conf.subscribe || conf.subscribe.delayHandle) return

    const delayedSubscribeValues = delayedSubscribeValuesRef.current
    while (delayedSubscribeValues.length) {
      const value = delayedSubscribeValues.shift()
      conf.subscribe.handleValue(value!) // T could be `undefined`
    }
  }, [confRef, conf.subscribe?.delayHandle])
}

const getSerializer = <T>(conf: PubSubConfig<T>) => (conf.serializer ?? new NoopStringSerializer()) as Serializer<T>

function getPubSubValue(pubsub: Record<string, unknown>, key: string | null | undefined) {
  if (!key) return null
  if (!(key in pubsub)) return null

  const value = pubsub[key]
  if (value == null) return null

  return String(value)
}
